import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useUser } from '@clerk/react';

// ── Types ─────────────────────────────────────────────────────────────────────
export interface TeacherProfile {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  school: string | null;
  district: string | null;
  state: string | null;
  grades: string[];
  subjects: string[];
  defaultDuration: number;
  defaultLessonTheme: string;
  defaultActivityTheme: string;
  defaultPresentationTheme: string;
  defaultAssessmentTheme: string;
  referralCode: string | null;
  referredBy: string | null;
  referralCredits: number;
  lifetimeReferralCount: number;
  billingCustomerId: string | null;
  onboardingCompletedAt: string | null;
  onboardingSkippedAt: string | null;
  subscriptionStatus: string;
  trialStartDate: string | null;
  trialEndDate: string | null;
}

interface ProfileContextType {
  profile: TeacherProfile | null;
  loading: boolean;
  updateProfile: (updates: Partial<Omit<TeacherProfile, 'id' | 'email' | 'referralCode' | 'subscriptionStatus'>>) => Promise<void>;
  isProfileComplete: boolean;
  /** True when onboarding was completed or explicitly skipped (persisted in DB). */
  onboardingDone: boolean;
}

const ProfileContext = createContext<ProfileContextType>({
  profile: null,
  loading: true,
  updateProfile: async () => {},
  isProfileComplete: false,
  onboardingDone: false,
});

export function useProfile() {
  return useContext(ProfileContext);
}

// ── Provider ──────────────────────────────────────────────────────────────────
export function ProfileProvider({ children }: { children: React.ReactNode }) {
  const { user, isLoaded } = useUser();
  const [profile, setProfile] = useState<TeacherProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isLoaded) return;
    if (!user) { setProfile(null); setLoading(false); return; }

    const email = user.primaryEmailAddress?.emailAddress ?? '';
    fetch(`/api/users/me?email=${encodeURIComponent(email)}`)
      .then(r => r.ok ? r.json() : null)
      .then((data: TeacherProfile | null) => {
        setProfile(data);
      })
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  }, [user, isLoaded]);

  const updateProfile = useCallback(async (
    updates: Partial<Omit<TeacherProfile, 'id' | 'email' | 'referralCode' | 'subscriptionStatus'>>
  ) => {
    try {
      const res = await fetch('/api/users/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (res.ok) {
        const updated: TeacherProfile = await res.json();
        setProfile(updated);
      }
    } catch {
      // silent — profile update failures are non-blocking
    }
  }, []);

  const isProfileComplete = Boolean(
    profile?.state && profile.grades.length > 0 && profile.subjects.length > 0
  );

  const onboardingDone = Boolean(
    profile?.onboardingCompletedAt || profile?.onboardingSkippedAt
  );

  return (
    <ProfileContext.Provider value={{ profile, loading, updateProfile, isProfileComplete, onboardingDone }}>
      {children}
    </ProfileContext.Provider>
  );
}
